import React, {useState, useEffect} from 'react';
import {
    Backdrop,
    BackdropProps,
    Button,
    Card,
    CardHeader,
    CircularProgress,
    Container,
    Grid,
    LinearProgress,
    Paper,
    Typography,
} from '@mui/material';
import '../../componentCss/Loading.scss';

export const Spinner = (props) => (
    <div className="spinner-pulse">
        <div className="spinner">
            <div></div>
            <div></div>
            <div></div>
        </div>
    </div>
);

export const CenteredSpinner = () => {
    return (
        <Grid container justifyContent="center">
            <Grid item>
                <Spinner></Spinner>
            </Grid>
        </Grid>
    );
};

export const FullScreenLoading = ({show}) => {
    const [delayShow, setDelayShow] = useState(false);
    useEffect(() => {
        setDelayShow(false);
        const timeout = setTimeout(() => setDelayShow(true), 500);
        return () => clearTimeout(timeout);
    }, [show]);

    return (
        <div>
            <div
                className={`spinner-fs-background${
                    show && delayShow ? ' show' : ''
                }`}></div>
            <div
                className={`spinner-fs-foreground${
                    show && delayShow ? ' show' : ''
                }`}>
                <Spinner></Spinner>
            </div>
        </div>
    );
};

export const Loading = ({
    children,
    promise,
    loadingComponent = () => <CenteredSpinner></CenteredSpinner>,
}) => {
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        let cancel = false;

        const f = async (cancel) => {
            try {
                await promise(cancel);
            } catch (error) {
                console.log(error);
            }

            setLoading(false);
        };

        f(cancel);

        return () => {
            cancel = false;
        };
    }, []);

    if (loading) {
        return loadingComponent();
    }

    return children;
};

interface ControlledLoadingProps {
    children?;
    loading: boolean;
    loadingComponent?: () => React.ReactNode;
}

export const ControlledLoading = ({
    loading,
    children = null,
    loadingComponent = () => <LoadingSpinner></LoadingSpinner>,
}: ControlledLoadingProps) => {
    if (loading) {
        return loadingComponent();
    }

    return children ?? <></>;
};

export const LoadingSpinner = () => {
    return (
        <Container>
            <Paper>
                <CenteredSpinner />
            </Paper>
        </Container>
    );
};

//Wrapper for Material UI Backdrop and Circular Spinner so we only need to define "open" prop
export const OverlaySpinner = (props: BackdropProps) => {
    return (
        <Backdrop
            sx={{color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1}}
            {...props}>
            <CircularProgress color="inherit" />
        </Backdrop>
    );
};

//Add progress bar props to the backdrop so we can have a full screen progress bar
interface ProgressBarProps extends BackdropProps {
    value: number;
    caption: string;
}

//Wrapper for Material UI Backdrop and Linear Progress so we only need to define "open" prop
export const ProgressBar = (props: ProgressBarProps) => {
    const {value, caption, ...backdropProps} = props;

    return (
        <Backdrop
            sx={{color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1}}
            {...backdropProps}>
            <Grid container>
                <Grid item xs={12} textAlign="center">
                    <Typography variant="body2">{caption}</Typography>
                </Grid>
                <Grid item xs={2} />
                <Grid item xs={8}>
                    <LinearProgress
                        color="inherit"
                        variant="determinate"
                        value={value}
                    />
                </Grid>
                <Grid item xs={2} />
            </Grid>
        </Backdrop>
    );
};

interface QueueBannerProps {
    show: boolean;
    caption: string;
    reloadFn: () => void;
}

//Wrapper for showing a "Queue in progress" banner with more info and a reload button
export const QueueBanner = (props: QueueBannerProps) => {
    if (!props.show) {
        return null;
    }

    return (
        <Card>
            <CardHeader
                title={
                    <Grid container>
                        <Grid item xs={12} sm={10}>
                            {props.caption}
                        </Grid>
                        <Grid item xs={12} sm={2}>
                            <Button
                                id="btnRefreshQueue"
                                fullWidth
                                onClick={props.reloadFn}>
                                Reload
                            </Button>
                        </Grid>
                    </Grid>
                }
            />
        </Card>
    );
};
